import { holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card.js';
import { Switch } from '../../components/ui/Switch.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

/**
 * 377 (#1233, design `Hosts.html` B): a Host membership's "Can see sales". With it on, the host sees their supplier's
 * Sales Statement and the dashboard's money figures; the supplier's owner is a Host with it on. Only an administrator
 * changes it (`PUT /memberships/:id/sales-access`, recorded on the person's history); others see it read-only.
 */
export function SalesAccessCard({
  membershipId,
  membershipRole,
  supplierName,
  canSeeSales,
}: {
  membershipId: string;
  membershipRole: string;
  supplierName: string;
  canSeeSales: boolean;
}) {
  const ability = useAbility();
  const queryClient = useQueryClient();
  const canEdit = holdsAnywhere(ability as never, 'update', 'Membership');
  const save = useMutation({
    mutationFn: (next: boolean) =>
      api.put<{ can_see_sales: boolean }>(`/memberships/${membershipId}/sales-access`, {
        can_see_sales: next,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.membership.one(membershipId) });
      await queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.membership.list });
    },
  });
  if (membershipRole !== 'Host') return null;
  const on = save.isPending ? !canSeeSales : canSeeSales;
  const who = supplierName || 'the supplier';

  return (
    <Card>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-steel">Sales</h2>
      <div className="flex items-start gap-3 rounded-lg border border-hairline p-3">
        <Switch
          checked={on}
          ariaLabel="Can see sales"
          disabled={!canEdit || save.isPending}
          onChange={() => save.mutate(!canSeeSales)}
        />
        <span>
          <span className="font-semibold text-ink">Can see sales</span>
          <span data-testid="sales-access-hint" className="block text-xs text-steel">
            {on
              ? `Sees ${who}'s Sales Statement and its dashboard figures.`
              : 'Handles the cars they are host of; no money figures.'}{' '}
            {canEdit
              ? 'Turn on for the owner, or anyone the supplier trusts with its money.'
              : 'Only an administrator changes this.'}
          </span>
        </span>
      </div>
      {save.isError && (
        <p className="mt-2 text-sm text-red-600">
          {(save.error as { message?: string }).message ?? 'Could not save.'}
        </p>
      )}
    </Card>
  );
}
